/**
 * Členové skupiny/projektu GitLabu — list + změna role + přidání + odebrání.
 * Tool: gitlab.member_set.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2, UserPlus } from 'lucide-react';
import { Button, Input } from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';

const ACCESS = ['guest', 'reporter', 'developer', 'maintainer', 'owner'] as const;

interface Member {
  id: number;
  username: string;
  access: string;
  state?: string;
}

export function MembersBlock({
  scope,
  connectionId,
  target,
  targetRef,
}: {
  scope: EffectiveScope;
  connectionId?: string;
  target: 'project' | 'group';
  targetRef: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [access, setAccess] = useState('developer');
  const key = ['gitlab-members', target, targetRef, connectionId ?? ''];
  const conn = connectionId ? { connection_id: connectionId } : {};

  const q = useQuery<{ ok?: boolean; error?: string; members?: Member[] }>({
    queryKey: key,
    queryFn: () =>
      callWidgetTool(
        'gitlab.member_set',
        { action: 'list', target, target_ref: targetRef, ...conn },
        scope,
        { silent: true },
      ),
    enabled: !!targetRef,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const mut = useMutation({
    mutationFn: (args: {
      action: 'add' | 'update' | 'remove';
      username: string;
      access?: string;
    }) =>
      callWidgetTool<{ ok?: boolean; error?: string }>(
        'gitlab.member_set',
        {
          action: args.action,
          target,
          target_ref: targetRef,
          username: args.username,
          ...(args.access ? { access: args.access } : {}),
          ...conn,
        },
        scope,
      ),
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('gitlab.members.failed'));
        return;
      }
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const accessOptions = ACCESS.map((a) => ({
    value: a,
    label: t(`gitlab.access.${a}`),
  }));

  const onRemove = async (m: Member) => {
    const ok = await themedConfirm({
      title: t('gitlab.members.removeTitle', { name: m.username }),
      description: t('gitlab.members.removeDesc'),
      confirmLabel: t('gitlab.members.removeConfirm'),
    });
    if (!ok) return;
    mut.mutate({ action: 'remove', username: m.username });
  };

  const members = q.data?.members ?? [];

  return (
    <div className='rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 space-y-2'>
      <div className='text-xs text-zinc-400'>
        {t('gitlab.members.title', { count: members.length })}
      </div>
      {q.isLoading
        ? <div className='text-xs text-zinc-500'>{t('common.loading')}</div>
        : q.data?.ok === false
        ? <div className='text-xs text-red-400'>{q.data.error ?? t('gitlab.members.failed')}</div>
        : members.length === 0
        ? <div className='text-xs text-zinc-500'>{t('gitlab.members.empty')}</div>
        : (
          <div className='space-y-1'>
            {members.map((m) => (
              <div key={m.id} className='flex items-center gap-2 text-sm'>
                <span className='font-mono text-zinc-200 min-w-0 flex-1 truncate'>
                  {m.username}
                  {m.state && m.state !== 'active' ? ` · ${m.state}` : ''}
                </span>
                <Select
                  size='sm'
                  value={m.access}
                  disabled={mut.isPending}
                  options={accessOptions}
                  onChange={(e) => {
                    const next = (e.target as HTMLSelectElement).value;
                    if (next && next !== m.access) {
                      mut.mutate({ action: 'update', username: m.username, access: next });
                    }
                  }}
                  aria-label={t('gitlab.members.roleAria', { name: m.username })}
                />
                <Button
                  variant='ghost'
                  size='sm'
                  disabled={mut.isPending}
                  onClick={() => void onRemove(m)}
                  title={t('gitlab.members.remove')}
                >
                  <Trash2 size={13} />
                </Button>
              </div>
            ))}
          </div>
        )}
      <div className='flex flex-col sm:flex-row gap-2 pt-1'>
        <Input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('gitlab.members.usernamePlaceholder')}
          className='flex-1 text-xs'
        />
        <Select
          size='sm'
          value={access}
          options={accessOptions}
          onChange={(e) => setAccess((e.target as HTMLSelectElement).value)}
        />
        <Button
          variant='primary'
          size='sm'
          loading={mut.isPending}
          disabled={!username.trim()}
          onClick={() => {
            mut.mutate(
              { action: 'add', username: username.trim(), access },
              { onSuccess: (r) => { if (r?.ok !== false) setUsername(''); } },
            );
          }}
        >
          <UserPlus size={13} /> {t('gitlab.members.add')}
        </Button>
      </div>
    </div>
  );
}
